import { track } from "utils/analytics"
import fetchDependencies from "./helpers/fetch-dependencies"
import { currentUtmParams, trackCheckoutPurchase } from "./services/analytics"
import { openingParams, restoreOpening, startOpening } from "./services/opening"

export async function init(options = {}) {
  const { Vue, VueI18n, data } = await fetchDependencies()
  const { default: signup } = await import(/* webpackChunkName: 'sign-up' */ "./signup")
  signup(Vue, VueI18n, data, options)
}

const { FCGON } = window

const PAGE_SUBSCRIBE_TOUCHPOINT = "page_subscribe"
// sign_up_immediately is set on the web only by the rewards page
const PAGE_REWARDS_TOUCHPOINT = "page_rewards"

// The inline sign-up app of the /subscribe page, when there is one.
let pageAppNode = null

function checkoutStatus() {
  const params = new URLSearchParams(window.location.search) // eslint-disable-line compat/compat
  return params.get("checkout")
}

function hasCheckoutResult() {
  const status = checkoutStatus()
  return status === "success" || status === "cancelled"
}

// A new opening reports paywall_view. The return from Stripe is no new
// opening: it restores the one that led to the checkout.
function openingFor(touchpoint) {
  if (hasCheckoutResult()) return restoreOpening()

  const opening = startOpening(touchpoint, Boolean(FCGON && FCGON.logged_in))
  track("paywall_view", openingParams(opening))
  return opening
}

// The touchpoint of a paywall trigger: data-touchpoint on the trigger or on
// the nearest container around it.
function touchpointOf(node) {
  const host = node.closest("[data-touchpoint]")
  if (host) return host.dataset.touchpoint

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn("Paywall trigger without data-touchpoint", node)
  }
  return "unknown"
}

export function showSignup(options = {}) {
  if (FCGON.is_app) {
    const { callNativeApp } = window
    if (callNativeApp) {
      callNativeApp("subscribe")
      return
    }
  }
  // While a subscription is in dunning (failed renewal being retried), the
  // member must fix billing - not start a new subscription. Every opener,
  // including programmatic ones like the hindcast lock, sends them to the My
  // membership page instead of the sign-up flow.
  if (FCGON && FCGON.subscription_billing_issue) {
    window.location.href = "/profile/subscription"
    return
  }
  // A page with its own sign-up app gets no second one: two apps would share
  // the social-auth listeners, the popup flag and the offer ids.
  if (pageAppNode) {
    pageAppNode.scrollIntoView({ behavior: "smooth", block: "start" })
    return
  }
  const { touchpoint, ...appOptions } = options
  init({ ...appOptions, opening: openingFor(touchpoint) })
}

window.showSignup = showSignup

function checkoutSessionId() {
  const params = new URLSearchParams(window.location.search) // eslint-disable-line compat/compat
  return params.get("session_id")
}

function trackCheckoutSuccessPurchase() {
  if (checkoutStatus() !== "success") return

  trackCheckoutPurchase(checkoutSessionId(), currentUtmParams())
}

function checkoutStepOptions() {
  const status = checkoutStatus()
  if (status === "success") {
    return { initialStep: "the-finish" }
  }
  if (status === "cancelled") {
    return { initialStep: FCGON && FCGON.logged_in ? "the-plan-selection" : "the-promo-signup" }
  }

  return {}
}

function cleanCheckoutResultFromUrl() {
  if (!window.history || !window.history.replaceState) return

  const url = new URL(window.location.href) // eslint-disable-line compat/compat
  url.searchParams.delete("checkout")
  url.searchParams.delete("session_id")
  window.history.replaceState(window.history.state, document.title, url.toString())
}

function mountSubscribe(node) {
  pageAppNode = node
  while (node.firstChild) {
    node.removeChild(node.firstChild)
  }
  const mountNode = document.createElement("div")
  node.appendChild(mountNode)
  init({
    el: mountNode,
    ...checkoutStepOptions(),
    variant: "page",
    opening: openingFor(PAGE_SUBSCRIBE_TOUCHPOINT),
  })
}

export default async function setListeners() {
  const subscribeNodes = Array.from(document.querySelectorAll(".js-subscribe"))
  subscribeNodes.forEach((node) => {
    mountSubscribe(node)
  })
  if (subscribeNodes.length > 0 && hasCheckoutResult()) {
    trackCheckoutSuccessPurchase()
    cleanCheckoutResultFromUrl()
  }

  const nodes = Array.from(document.querySelectorAll(".js-sign-up, .js-sign-up-no-free"))
  nodes.forEach((node) => {
    node.addEventListener("click", (event) => {
      event.preventDefault()
      showSignup({ touchpoint: touchpointOf(node) })
    })
  })
  // Automatic startup on the dedicated sign up page
  if (subscribeNodes.length === 0 && hasCheckoutResult()) {
    showSignup(checkoutStepOptions())
    trackCheckoutSuccessPurchase()
    cleanCheckoutResultFromUrl()
  } else if (FCGON && FCGON.sign_up_immediately) {
    init({ opening: openingFor(PAGE_REWARDS_TOUCHPOINT) })
  }
}
